/* istanbul ignore file */
/**
 * Created by rathompson on 7/25/22
 * File: CloneRoleModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { CSSProperties, useCallback, useContext, useEffect, useMemo, useState, VFC } from 'react'
import {
  Button,
  Modal,
  ModalFooter,
  ModalHeader,
  Body,
  Select, ModalProps, SelectItem,
} from '@panwds/react-ui'
import {
  BUTTON_CANCEL, BUTTON_CLONE_ROLE, CLONE_ROLE_DESC, CLONE_ROLE_INFO, PLACEHOLDER_SELECT_ROLE,
  TITLE_CLONE_ROLE,
} from '../../../constants/LabelConstants'
import styled from 'styled-components'
import GlobalContext from '../../../../common/context/GlobalContext'
import type { IAM_Role } from '../../../../common/reducers/types/IAMReducer.types'

const BUTTON_WRAPPER = styled.div`
  button {
    color: #333333;
    justify-content: space-between;
    width: 100%;
  }
`

const CloneRoleModal: VFC<ModalProps & {
  closeModal: () => void,
  onSubmit: Function
}> = ({ closeModal, isOpen, onSubmit }) => {

  const { iamReducer, treeReducer } = useContext(GlobalContext)
  const { roles = [], customRoles = [] } = iamReducer
  const { sgKeyBy = {}, selectedTreeNode = {} } = treeReducer

  const [selectedItem, setSelectedItem] = useState<SelectItem | null>(null)

  useEffect(() => {
    const modalMask = document.getElementsByClassName('tw-bg-blue-steel-700 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-80')
    if (modalMask && modalMask.length) {
      (modalMask[0] as HTMLElement).style.zIndex = '1051'
    }
    else {
      const mask = document.getElementsByClassName('tw-bg-gray-900 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-75')
      if (mask && mask.length) {
        (modalMask[0] as HTMLElement).style.zIndex = '1051'
      }
    }
  }, [isOpen])

  const items = useMemo(() => {
    return [...roles, ...customRoles].map((role: IAM_Role) => ({
      value: (
        <div className="tw-flex tw-gap-2">
          <Body>{role.label || role.name}</Body>
          {!!role?.tsg_id && role?.tsg_id !== selectedTreeNode?.id && (
            <Body appearance='secondary'>{`(inherited from ${sgKeyBy[role?.tsg_id]?.display_name})`}</Body>
          )}
        </div>
      ),
      role
    })) as unknown as SelectItem[]
  }, [customRoles, roles, selectedTreeNode?.id, sgKeyBy])

  const selectText = useMemo(() => {
    return selectedItem?.value || PLACEHOLDER_SELECT_ROLE
  }, [selectedItem])

  const onSubmitClick = useCallback(() => {
    onSubmit(selectedItem?.role)
    closeModal()
  }, [onSubmit, closeModal, selectedItem?.role])

  return (
    <Modal
      style={{ top: '50%', '--tw-translate-y': '-50%', minHeight: '335px', zIndex: 1052 } as CSSProperties}
      size='sm'
      isOpen={isOpen}
      onClose={closeModal}>
      <ModalHeader title={TITLE_CLONE_ROLE}/>
      <BUTTON_WRAPPER className='tw-flex tw-flex-col tw-gap-2 tw-font-lato tw-flex-1 tw-w-full tw-p-4 tw-text-default dark:tw-text-dark-bg'>
        <Body>{CLONE_ROLE_DESC}</Body>
        <Select
          addClassName='cmm-modal-select'
          fitToItemWidth
          items={items}
          selectedItem={selectedItem}
          onChange={({ selectedItem }) => {
            setSelectedItem(selectedItem)
          }}
          fullWidth
          button={<Button isMenu>{selectText}</Button>}
        />
        <Body>{CLONE_ROLE_INFO}</Body>
      </BUTTON_WRAPPER>
      <ModalFooter>
        <Button appearance='secondary' onClick={closeModal}>{BUTTON_CANCEL}</Button>
        <Button appearance='primary' disabled={!selectedItem} onClick={onSubmitClick}>{BUTTON_CLONE_ROLE}</Button>
      </ModalFooter>
    </Modal>
  )
}

CloneRoleModal.propTypes = {}
export default CloneRoleModal
